<template>
  <div v-if="jobStages && jobStages.length > 0"
    class="columns">
    <div class="column is-6">
      <article class="box">
        <p class="title">Totals</p>
        <quote-detail-summary v-if="!value.isAssessmentEnabled"
          v-model="value" />
        <quote-detail-assessment-summary v-else
          v-model="value" />
      </article>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash.clonedeep'
import { QuoteService } from '@/services'
import StoreMixin from './storeMixin'
import QuoteDetailSummary from './QuoteDetailSummary'
import QuoteDetailAssessmentSummary from './QuoteDetailAssessmentSummary'
import { InvoiceTypes, EventHubTypes } from '@/enums'
import { DateTime } from 'luxon'
import JobStageTypes from '@/enums/JobStageTypes'
import InnerQuoteService from './QuoteService'

export default {
  name: 'QuoteSummary',
  inject: ['$vv'],
  components: {
    QuoteDetailSummary,
    QuoteDetailAssessmentSummary
  },
  mixins: [StoreMixin],
  props: {
    value: null,
    insurer: null
  },
  data: () => {
    return {
      innerValue: null,
      selectedQuoteDate: null,
      selectedJobStage: null,
      sortKey: null,
      quoters: [],
      formats: {
        title: 'MMMM YYYY',
        weekdays: 'W',
        navMonths: 'MMM',
        input: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        data: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        dayPopover: 'L'
      },
      attrs: [
        {
          key: 'today',
          highlight: {
            backgroundColor: '#ff8080'
          },
          popover: {
            label: 'Today'
          },
          dates: new Date()
        }
      ],
      modelConfig: {
        start: {
          timeAdjust: '08:00:00'
        },
        end: {
          timeAdjust: '08:00:00'
        }
      },
      isPauseWatch: false,
      bookedInSortKey: null,
      dragValue: null,
      jobStartDate: null,
      jobEndDate: null,
      inspectionStartDate: null,
      inspectionEndDate: null,
      arrivedForRepairDate: null,
      isRepairLateReasonActive: false,
      oriJobStartDate: null,
      oriJobEndDate: null,
      quoteRepx: null,
      innerJobNSubJobStageCompositeKey: null,
      vehicleDepartedDate: null,
      repairBookedDate: null,
      isArrived: false,
      isDeparted: false
    }
  },
  computed: {
    $v() {
      return this.$vv || this.$v
    },
    selectDragAttribute() {
      return {
        popover: {
          visibility: 'hover',
          isInteractive: false // Defaults to true when using slot
        }
      }
    },
    filteredQuoters() {
      return this.quoters.filter((option) => {
        return option.toString().toLowerCase().indexOf(this.innerValue.quoter.toLowerCase()) >= 0
      })
    },
    canChangeReadOnly() {
      return this.$userInfo.isSupportUser || this.$userInfo.isCustomerAdministrator
    },
    invoice() {
      return this.innerValue.invoices.find((i) => !i.isNew && !i.deleted && i.invoiceType === InvoiceTypes.Quote)
    },
    excessInvoice() {
      return this.innerValue.invoices.find((i) => !i.isNew && !i.deleted && i.invoiceType === InvoiceTypes.Excess)
    },
    ormJobEndJobEndValidationText() {
      if (!this.$v.entity.jobStart.required || !this.$v.entity.jobEnd.required) {
        return 'Required by ORM'
      } else if (!this.$v.entity.jobStart.validJobStart || !this.$v.entity.jobEnd.validJobEnd) {
        return 'Invalid date range'
      }
    },
    assessmentHistoryToggleDisable() {
      if (this.value.quoteAssessments && this.value.quoteAssessments.length > 0) {
        return true
      }
      if (this.value.invoices && this.value.invoices.length > 0) {
        return true
      }
      if (this.value.orm && this.value.orm.messageNo != '0') {
        return true
      }
      if (this.value.subQuoteNos.length > 1) {
        return true
      }
      return false
    },
    isInspectionBookingDateExist() {
      return (
        this.innerValue.quoteCycleTime.inspectionBookingDate != null &&
        this.innerValue.quoteCycleTime.inspectionBookingDate != undefined &&
        this.innerValue.repx != null
      )
      // return this.innerValue.inspectionBookingStart != null && this.innerValue.inspectionBookingStart != undefined && this.innerValue.repx!=null
    },
    // isSubJobStagesAvailable() {
    //   return this.subJobStagesBySelectedJobStage != null && this.subJobStagesBySelectedJobStage.length > 0
    // },
    // subJobStagesBySelectedJobStage() {
    //   return this.subJobStages.filter((i) => i.jobStage == this.innerValue.jobStage)
    // },
    isRepairBooked() {
      return this.innerValue.isRepairBooked
    },
    isRepx() {
      return this.innerValue.repx != null
    },
    isRepairStartDateSynced() {
      return this.quoteRepx ? this.quoteRepx.isRepairStartDateSynced : false
    },
    // jobNSubJobStageCompositeKey() {
    //   let jobStage = this.innerValue.jobStage == null ? '' : ''
    //   let subJobStage = this.innerValue.subJobStage == null ? '' : ''
    //   return this.innerValue.jobStage || '' + '|' + subJobStage
    // }
    jobNSubJobStagesOptions() {
      const isDisableParentWithSub = !!this.isRepx
      const groupedStages = {}

      // Group job stages and sub job stages
      this.jobNSubJobStages.forEach((option) => {
        const jobStageId = option.jobStageId
        const jobStageName = option.jobStageName

        if (!groupedStages[jobStageId]) {
          // Get sort key from jobStages array
          const jobStageInfo = this.jobStages ? this.jobStages.find(js => js.stage === jobStageId) : null
          const jobStageSortKey = jobStageInfo ? jobStageInfo.sortKey : 999

          groupedStages[jobStageId] = {
            jobStageId,
            jobStageName,
            jobStageSortKey: jobStageSortKey,
            mainStage: {
              compositeKey: `${jobStageId}|`,
              compositeName: jobStageName,
              disabled: false
            },
            subStages: []
          }
        }

        // If this option has a sub job stage, add it to the subStages array
        if (option.subJobStageId && option.subJobStageId !== '') {
          groupedStages[jobStageId].subStages.push(option)
          // If there are sub stages, disable the parent stage if needed
          if (isDisableParentWithSub) {
            groupedStages[jobStageId].mainStage.disabled = true
          }
        }
      })

      // Convert to array and sort by jobStageSortKey
      const sortedGroups = Object.values(groupedStages).sort((a, b) => {
        return (a.jobStageSortKey || 999) - (b.jobStageSortKey || 999)
      })

      // Sort sub stages within each group by sortKey
      sortedGroups.forEach(group => {
        group.subStages.sort((a, b) => {
          return (a.sortKey || 999) - (b.sortKey || 999)
        })
      })

      return sortedGroups
    },

    //TEMP check for GEMCan company - REMOVE AFTER ASSESSMENT HISTORY PHASE 2
    tempGemCan() {
      return this.$userInfo.isCustomerAdministrator && this.$company.info.companyId === 'a5ffbdc0-41d2-4e7f-8ecf-e51beac57e6b'
    }
  },
  watch: {
    innerValue: {
      handler: function (newVal, oldVal) {
        if (!this.isPauseWatch) {
          this.$emit('input', newVal)
        }
      },
      deep: true
    },
    selectedQuoteDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.quoteDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.innerValue.quoteDate = null
      }
    },
    jobStartDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.jobStart = newVal.toISOString().split('.')[0] + 'Z'
        if (this.innerValue.jobEnd && this.sortKey < this.bookedInSortKey && this.insurer.autoBookedInForDueInOut) {
          this.selectedJobStage = JobStageTypes.BookedIn
          this.innerValue.jobStage = JobStageTypes.BookedIn
        }
      } else {
        this.innerValue.jobStart = null
      }
    },
    jobEndDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.jobEnd = newVal.toISOString().split('.')[0] + 'Z'
        //to prompt late reason
        this.isRepairLateReasonActive = this.compare1DateTo2(newVal, this.oriJobEndDate)
        if (this.innerValue.jobStart && this.sortKey < this.bookedInSortKey && this.insurer.autoBookedInForDueInOut) {
          this.selectedJobStage = JobStageTypes.BookedIn
          this.innerValue.jobStage = JobStageTypes.BookedIn
        }
      } else {
        this.innerValue.jobEnd = null
      }
    },
    arrivedForRepairDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.quoteCycleTime.arrivedForRepairDate = newVal.toISOString().split('.')[0] + 'Z'
        this.isArrived = true
        if (this.innerValue.quoteCycleTime.arrivedForRepairDate && isNaN(oldVal)) {
          this.innerValue.jobStage = JobStageTypes.VehicleArrived
        }
      } else {
        this.innerValue.quoteCycleTime.arrivedForRepairDate = null
      }
    },
    vehicleDepartedDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.quoteCycleTime.vehicleDepartedDate = newVal.toISOString().split('.')[0] + 'Z'
        this.isDeparted = true
        if (this.innerValue.quoteCycleTime.vehicleDepartedDate && isNaN(oldVal)) {
          this.innerValue.jobStage = JobStageTypes.VehicleReady
        }
      } else {
        this.innerValue.quoteCycleTime.vehicleDepartedDate = null
      }
    },
    repairBookedDate: function (newVal, oldVal) {
      if (newVal) {
        this.innerValue.quoteCycleTime.repairBookedDate = newVal.toISOString().split('.')[0] + 'Z'
        console.log(this.innerValue.quoteCycleTime.repairBookedDate)
      } else {
        this.innerValue.quoteCycleTime.repairBookedDate = null
      }
    }
  },
  async created() {
    this.getQuoters()
    this.innerValue = _cloneDeep(this.value)
    this.selectedQuoteDate = new Date(`${this.innerValue.quoteDate}`)
    this.selectedJobStage = this.innerValue.jobStage
    this.sortKey = this.getJobStageSortKey(this.innerValue.jobStage)
    this.bookedInSortKey = this.getJobStageSortKey(JobStageTypes.BookedIn)
    if (this.innerValue.jobStart) {
      this.jobStart = new Date(`${this.innerValue.jobStart}`)
      this.selectedJobStart = new Date(`${this.innerValue.jobStart}`)
      this.jobStartDate = new Date(`${this.innerValue.jobStart}`)
      this.oriJobStartDate = this.jobStartDate
    }
    if (this.innerValue.jobEnd) {
      this.jobEnd = new Date(`${this.innerValue.jobEnd}`)
      this.selectedJobEnd = new Date(`${this.innerValue.jobEnd}`)
      this.jobEndDate = new Date(`${this.innerValue.jobEnd}`)
      this.oriJobEndDate = this.jobEndDate
    }
    // if (this.innerValue.inspectionBookingStart) this.inspectionStartDate = new Date(`${this.innerValue.inspectionBookingStart}`)
    // if (this.innerValue.inspectionBookingEnd) this.inspectionEndDate = new Date(`${this.innerValue.inspectionBookingEnd}`)
    if (this.innerValue.quoteCycleTime.inspectionBookingDate) this.inspectionStartDate = new Date(`${this.innerValue.quoteCycleTime.inspectionBookingDate}`)
    if (this.innerValue.quoteCycleTime.arrivedForRepairDate) this.arrivedForRepairDate = new Date(`${this.innerValue.quoteCycleTime.arrivedForRepairDate}`)
    if (this.innerValue.quoteCycleTime.vehicleDepartedDate) this.vehicleDepartedDate = new Date(`${this.innerValue.quoteCycleTime.vehicleDepartedDate}`)
    if (this.innerValue.quoteCycleTime.repairBookedDate) this.repairBookedDate = new Date(`${this.innerValue.quoteCycleTime.repairBookedDate}`)

    this.setInscopeDefault()
    await this.loadQuoteRepxIfExist()
  },
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, async () => {
      this.isPauseWatch = true
      this.innerValue = _cloneDeep(this.value)
      await this.loadQuoteRepxIfExist()
      this.isPauseWatch = false
    })

    let jobStage = this.innerValue.jobStage
    if (jobStage) {
      let subJobStage = this.innerValue.subJobStage || ''
      this.innerJobNSubJobStageCompositeKey = jobStage + '|' + subJobStage
    }
  },

  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
  },
  methods: {
    compare1DateTo2(dateStr1, dateStr2) {
      if (dateStr1 && dateStr2) {
        const date1 = new Date(dateStr1)
        const date2 = new Date(dateStr2)

        if (date1 > date2) {
          return true
        }
      }

      return false
    },
    quoteDateInputEvent(event) {
      this.selectedQuoteDate = this.$filters.fromISOWithCurrentTime(event.toISOString())
    },
    inScopeOnChange(event) {
      this.innerValue.inScope = !!event.target.checked
    },
    driveableOnChange(event) {
      this.innerValue.driveable = !!event.target.checked
    },
    assessmentHistoryChange(event) {
      this.innerValue.isAssessmentEnabled = !!event.target.checked
    },
    clearArrivedForRepairDate() {
      if (this.arrivedForRepairDate !== null) {
        this.arrivedForRepairDate = null
        this.isArrived = false
      }
    },
    clearVehicleDepartedDate() {
      if (this.vehicleDepartedDate !== null) {
        this.vehicleDepartedDate = null
        this.isDeparted = false
      }
    },
    clearRepairBookedDate() {
      if (this.repairBookedDate !== null) {
        this.repairBookedDate = null
      }
    },
    resetJobStartEnd() {
      if (!this.isRepx || !this.isRepairStartDateSynced) {
        if (this.jobStartDate !== null) {
          this.jobStartDate = null
        }
      }
      if (this.jobEndDate !== null) {
        this.jobEndDate = null
      }

      if (this.selectedJobStage == JobStageTypes.BookedIn && this.insurer.autoBookedInForDueInOut) {
        this.selectedJobStage = JobStageTypes.FollowUpRequired
        this.innerValue.jobStage = JobStageTypes.FollowUpRequired
      }

      // Reset validation objects
      this.$v.entity.jobStart.$reset()
      this.$v.entity.jobEnd.$reset()
    },
    jobStartEndDragEvent(event) {
      this.dragValue = event
    },
    jobStartEndInputEvent(event) {},
    async getQuoters() {
      this.quoters = await QuoteService.getQuoters()
    },
    formatJobStage(value) {
      if (value.toLowerCase() === 'none') {
        return '-- Job Stage --'
      } else {
        return value
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
          .replace(/^./, function (str) {
            return str.toUpperCase()
          })
      }
    },
    formatSubJobStage(value) {
      if (value.toLowerCase() === '' || value == null) {
        return '-- Sub Job Stage --'
      } else {
        return value
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
          .replace(/^./, function (str) {
            return str.toUpperCase()
          })
      }
    },
    displayJobStage(jobNSubJobStages) {
      const { compositeName, subJobStageId, subJobStageName } = jobNSubJobStages
      const hasSubJobStage = subJobStageId
      let name = hasSubJobStage ? subJobStageName : compositeName
      if (name.toLowerCase() === '' || name == null) {
        return '-- Sub Job Stage --'
      } else {
        name = this.formatJobStage(name)
      }
      return hasSubJobStage ? `\u00A0\u00A0\u00A0 ${name}` : name
    },
    toogleReadOnly() {
      if (this.canChangeReadOnly) {
        this.innerValue.readOnly = !this.innerValue.readOnly
        this.innerValue.readOnlyStatuses.splice(this.innerValue.subQuoteNo, 1, this.innerValue.readOnly)
      }
    },
    toggleActive(active) {
      this.innerValue.deleted = !this.innerValue.deleted
    },
    getNoOfDays(startDate, endDate) {
      const d1 = DateTime.fromISO(startDate.toISOString())
      const d2 = DateTime.fromISO(endDate.toISOString())
      return d2.diff(d1, 'days').toObject().days + 1
    },
    getJobStageSortKey(stageId) {
      const index = this.jobStages.findIndex((i) => i.stage === stageId)
      const sortKey = this.jobStages[index].sortKey
      return sortKey
    },
    setInscopeDefault() {
      if (this.innerValue.isNew && this.insurer.inScope) {
        this.innerValue.inScope = this.insurer.inScope
      }
    },
    jobStartDateInputEvent(event) {},
    jobEndDateInputEvent(event) {},
    handleJobStageChanged() {
      this.innerValue.subJobStage = null
    },
    handleJobNSubJobStageChanged(event) {
      const selected = event.target.value
      if (selected && selected.includes('|')) {
        let result = selected.split('|')
        this.innerValue.jobStage = result[0] || null
        this.innerValue.subJobStage = result[1] || null
      } else {
        this.innerValue.jobStage = null
        this.innerValue.subJobStage = null
      }
    },
    handleRepairLateReasonClose() {
      this.isRepairLateReasonActive = false
    },
    async loadQuoteRepxIfExist() {
      const quoteRepx = await InnerQuoteService.getQuoteRepx(this.innerValue.quoteId)
      if (quoteRepx) {
        this.quoteRepx = quoteRepx
      }
    },
    isArrivedOnChange(event) {
      this.isArrived = !!event.target.checked
      if (!this.isArrived) {
        this.clearArrivedForRepairDate()
      }
      else {
        this.arrivedForRepairDate = new Date()
      }
    },
    isDepartedOnChange(event) {
      this.isDeparted = !!event.target.checked
      if (!this.isDeparted) {
        this.clearVehicleDepartedDate()
      }
      else {
        this.vehicleDepartedDate = new Date()
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>

<style lang="scss" scoped>
.readonly-pointer {
  cursor: pointer;
}
</style>
